import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Styles from '../ApiKeys.module.scss';
import {
	Input,
	Toast as notificationPopUp,
	TextArea,
	Button,
	Checkbox,
	Row,
	Col,
	Toggle,
	Loader
} from '@kaleyra/emmy';
import { isEmpty } from 'lodash';
import ErrorIcon from '../../../../../assets/error.svg';
import { IAPIKeys } from '../../../../../interface';
import { checkIfValidIPV6, checkWhiteListedApiKeyLimit, limitStringLength, removeBackSlash, validateIpAddress } from '../../../../../utils';
import APIDetails from '../APIDetails';
import { range } from 'lodash';
import SelectConfigurationDropDown from '../../components/IpConfigurationDropdown.tsx/index'
import NotificationPopUp from '../../components/NotificationPopup'

interface Props {
	onCancel: () => void;
	item: any;
	save: boolean;
	onDisable: (value: boolean) => void;
	onResetSave: () => void;
}
export type MapStateType = {
	apiKeyData: IAPIKeys[];
};

export type DispatchPropsType = {
	onFetchSingleItem: (data: any) => void;
	onEditKey: (data: any, callBack: any) => void;
};

type PropsType = MapStateType & DispatchPropsType & Props;

const ApiKeysEditForm: React.FC<PropsType> = ({
	apiKeyData,
	onFetchSingleItem,
	onCancel,
	item,
	onEditKey,
	save,
	onDisable,
	onResetSave,
}) => {
	const { t } = useTranslation();
	const [title, setTitle] = useState('');
	const [keywords, setkeywords] = useState<any | null>(null);
	const [statusCheck, setStatusCheck] = useState(false);
	const [host, setHost] = useState('');
	const [sid, setSid] = useState('');
	const [titleErr, setTitleErr] = useState(false);
	const [ipErr, setIpErr] = useState(false);
	const [whitelistIpToggle, setWhiteListIpToggle] = useState(false);
	const [configuration, setConfiguration] = useState<any>();
	const [showToast, setShowToast] = useState(true);
	const [dropDown, setDropDown] = useState(false);
	const [saving, setSave] = useState(false);

	const allowedIpRangeIpv4 = range(8, 31);
	const allowedIpRangeIpv6 = range(1, 129);

	useEffect(() => {
		if (!isEmpty(item)) {
			setTitle(item?.api_name);
			setStatusCheck(getStatus());
			onFetchSingleItem({ api_key_id: item?.id });
		}
	}, [item]);

	useEffect(() => {
		if (apiKeyData?.length && !saving) {
			if (apiKeyData[0]?.whitelisted_ips?.length) {
				setkeywords(apiKeyData[0]?.whitelisted_ips);
				setWhiteListIpToggle((apiKeyData[0]?.whitelisted_ips.length) ? true : false);
				setConfiguration(null);
			}
			else if (apiKeyData[0]?.ip_config?.name) {
				setWhiteListIpToggle((apiKeyData[0]?.ip_config?.name) ? true : false);
				setConfiguration(apiKeyData[0]?.ip_config);
				setkeywords(null);
			}
			else {
				setkeywords(null);
				setWhiteListIpToggle(false);
			}
			setHost(apiKeyData[0]?.host);
			setSid(apiKeyData[0]?.sid);
		}
	}, [apiKeyData]);

	useEffect(() => {
		return () => {
			setkeywords(undefined);
		};
	}, []);

	useEffect(() => {
		if (save) {
			handleSaveClick();
		}
	}, [save]);

	useEffect(() => {
		onDisable(checkSaveButtonEnabled());
	}, [title, keywords, configuration, whitelistIpToggle]);

	const validateCheck = (field: string) => {
		if (field === 'title') {
			if (!title?.length || title?.length < 3 || title?.length > 50) {
				return true;
			}
		} else if (field === 'keywords') {
			if (keywords?.length) {
				let res = keywords.split(',');
				const duplicateIps = res.filter((item: any, index: number) => res.indexOf(item) !== index);

				if (res.length > checkWhiteListedApiKeyLimit()) {
					setIpErr(true);
					notificationPopUp(
						t("KLR02668", { "maxIpCount": checkWhiteListedApiKeyLimit() }),
						'error'
					);

					return false;
				}
				if (duplicateIps.length > 0) {
					setIpErr(true);
					notificationPopUp(
						t("KLR02669"),
						'error'
					);

					return false;
				}
				for (let i = 0; i < res.length; i++) {

					let ip = res[i].split('/');
					if (res[i].includes('/')) {
						if (!validateIpAddress((ip)[0])
							|| ip.includes('')
							|| (checkIfValidIPV6((ip)[0]) ? !allowedIpRangeIpv6.includes(Number(ip[1])) : !allowedIpRangeIpv4.includes(Number(ip[1])))
						) {
							setIpErr(true);
							notificationPopUp(
								"Provide valid IP's to proceed",
								'error'
							);
							return false;
						}
					}
					else {
						if (!validateIpAddress((ip)[0])) {
							setIpErr(true);
							notificationPopUp(
								"Provide valid IP's to proceed",
								'error'
							);
							return false;
						}
					}
				}
			}
			return true;
		}
		return false;
	};
	const handleTitleOnBlur = (e: any) => {
		if (validateCheck('title')) {
			setTitleErr(true);
			return;
		}
		setTitleErr(false);
	};

	const handleSaveClick = () => {
		onResetSave();
		onDisable(true);
		setSave(true);
		if (!validateCheck('title') && validateCheck('keywords')) {
			let data = {
				id: apiKeyData[0]?.id,
				api_name: title,
				activity_log_action: "Edited",
				activity_log_object: "API KEYS",
				activity_log_object_value: title,
				whitelisted_ips: (keywords && whitelistIpToggle) ? keywords : '',
				ip_config_name: (configuration && whitelistIpToggle) ? configuration?.name : '',
				status: statusCheck ? 'on' : ' '
			};
			onEditKey(data, (resp: any, stat: boolean) => {
				onDisable(false);
				if (!isEmpty(resp)) {
					if (resp?.status === 400) {
						notificationPopUp(
							resp?.data?.message,
							'error',
							() => null
						);
					} else {
						setSave(false)
						onCancel();
						notificationPopUp(
							t('apiUpdateSuccess'),
							'success',
							() => null
						);
					}
				}
			});
		} else {
			onDisable(false);
		}
	};
	const getStatus = () => {
		if (item?.status === 1 || item?.status === 'on') {
			return true;
		} else if (item?.status === 2) {
			return false;
		} else {
			return false;
		}
	};
	const checkSaveButtonEnabled = () => {
		if (!title.length || title?.length < 3 || title?.length > 50) {
			return true;
		}
		if (whitelistIpToggle) {
			if (!configuration?.name && !keywords?.length) {
				return true;
			}
			if (!configuration?.name) {
				if (keywords?.length > 0 && ipErr) return true;
			}
			return false;
		}
		return false;
	};

	const validateIps = (keywords: any) => {
		if (keywords?.length) {

			let res = keywords.split(',');
			const duplicateIps = res.filter((item: any, index: number) => res.indexOf(item) !== index);

			if (res.length > checkWhiteListedApiKeyLimit()) {
				return true;
			}
			if (duplicateIps.length > 0) {
				return true;
			}
			for (let i = 0; i < res.length; i++) {
				let ip = res[i].split('/')
				if (res[i].includes('/')) {
					if (!validateIpAddress((ip)[0])
						|| ip.includes('')
						|| (checkIfValidIPV6((ip)[0]) ? !allowedIpRangeIpv6.includes(Number(ip[1])) : !allowedIpRangeIpv4.includes(Number(ip[1])))
					) {
						return true;
					}
				}
				else {
					if (!validateIpAddress((ip)[0])) {
						return true;
					}
				}
			}
		}
		return false;
	};

	const handleMultOnBlur = (keywords: any) => {
		if (validateIps(keywords)) {
			setIpErr(true);
			return;
		}
		setIpErr(false);
	};
	const validateFields = (field: string): 'error' | undefined => {
		if (field === 'title') {
			if (title === '' || title?.length < 3 || title?.length > 50) {
				return 'error';
			}
		}
		if (field === 'keywords') {
			if (validateIps(keywords)) {
				return 'error';
			}
		}
		return undefined;
	};

	const setErrors = (field: string) => {
		if (field === 'title') {
			if (!title || title.trim().length === 0) {
				return (
					<>
						<img
							src={ErrorIcon}
							alt=""
							className={Styles.errorIcon}
						/>
						{t('titleReq')}
					</>
				);
			} else if (title.trim().length < 3) {
				return (
					<>
						<img
							src={ErrorIcon}
							alt=""
							className={Styles.errorIcon}
						/>
						{t('min3Char')}
					</>
				);
			}
			else if(title.trim().length > 50){
				return (
					<>
						<img
							src={ErrorIcon}
							alt=""
							className={Styles.errorIcon}
						/>
						{t('KLR02818')}
					</>
				);
			}
		}

		if (field === 'keywords') {
			let res = keywords.split(',');
			const duplicateIps = res.filter((item: any, index: number) => res.indexOf(item) !== index);
			if (res.length > checkWhiteListedApiKeyLimit()) {
				return (
					<>
						<img
							src={ErrorIcon}
							alt=""
							className={Styles.errorIcon}
						/>
						{t("KLR02668", { "maxIpCount": checkWhiteListedApiKeyLimit() })}
					</>
				)
			}
			if (validateIps(keywords)) {
				if (duplicateIps.length > 0) {
					return (
						<>
							<img
								src={ErrorIcon}
								alt=""
								className={Styles.errorIcon}
							/>
							{t("KLR02669")}
						</>
					)
				}
				return (
					<>
						<img
							src={ErrorIcon}
							alt=""
							className={Styles.errorIcon}
						/>
						{t('apiValidIp')}
					</>
				);
			}
		}
	};


	const renderApiDetails = () => {
		return (
			<>
				<APIDetails
					host={host}
					sid={sid}
					apiKey={item?.api_key_full}
				></APIDetails>
				<Button
					type="secondary"
					onClick={() =>
						window.open('https://developers.kaleyra.io', '__blank')
					}
					className={Styles.documentEdit}
					small
				>
					<span>{t('readDocument')}</span>
				</Button>
			</>
		);
	};

	return (
		<>
			{
				!apiKeyData?.length ? <Loader />
					:
					<div className={Styles.formContainer}>
						<Checkbox
							checked={statusCheck}
							onChange={(e: any) => setStatusCheck(e.target.checked)}
							className={Styles.chk}
						/>
						<span className={Styles.chkLabel}>{t('mainActive')}</span>
						<p className={Styles.apiKeyName}>{t("KLR02702")}</p>
						<Input
							placeholder={t('mainName')}
							label={t('mainName')}
							value={title}
							bottomLabelType={titleErr ? validateFields('title') : undefined}
							bottomLabel={titleErr ? setErrors('title') : false}
							onChange={(e: any) => {
								setTitleErr(false);
								setTitle(e.target.value);
							}}
							onBlur={(e: any) => {
								handleTitleOnBlur(e.target.value);
							}}
							showBottomLabelSpace={titleErr ? true : false}
						/>
						<Row>
							<Col size={10}>
								<div className={Styles.whitelistIPHead}>{t("KLR02666")}</div>
							</Col>
							<Col size={2}>
								<div className={Styles.whitelistToggle}>
									<Toggle
										checked={whitelistIpToggle}
										onChange={() => {
											setWhiteListIpToggle(!whitelistIpToggle)
											setkeywords(null)
											setConfiguration(null)
										}}
									/>
								</div>
							</Col>
						</Row>
						<div className={Styles.rangeWhitelistDesc}>{t("KLR02667")}</div>

						{
							whitelistIpToggle &&
							<div className={Styles.rowE}>
								<Row>
									<Col size={7}>
										<div className={Styles.whitelistedIp}>
											{
												configuration &&
												<>
													<div>Selected IP Config</div>
													<div className={Styles.selectedConfig}>{limitStringLength(configuration?.name, 32)}</div>
												</>
											}
											<div>{t("KLR02703")}</div>
										</div>

									</Col>
									<Col size={5}>
										<div className={Styles.importIpConfig}>
											<SelectConfigurationDropDown
												config={configuration}
												setConfig={setConfiguration}
												dropDown={dropDown}
												setDropDown={setDropDown}
												onClose={onCancel}
												keyword={setkeywords}
											/>
										</div>
									</Col>
								</Row>
								{
									configuration ?
										<div className={Styles.taWhitelistC}>
											<TextArea
												bordered
												value={removeBackSlash(configuration?.ip_addresses || '')}
												showBottomLabelSpace={false}
												disabled
											/>
										</div>
										:
										<>
											<TextArea
												bordered
												bottomLabelType={ipErr ? validateFields('keywords') : undefined}
												bottomLabel={ipErr ? setErrors('keywords') : false}
												label={t("KLR02695")}
												onChange={(event: any) => {
													setIpErr(false);
													setkeywords(event.target.value.replace(/\s/g, ''));
												}}
												onBlur={(event: any) => {
													handleMultOnBlur(event.target.value.replace(/\s/g, ''));
												}}
												placeholder={t('IPplaceholder')}
												value={keywords}
												showBottomLabelSpace={ipErr}
												className={Styles.taWhitelistC}
											/>
											<div className={Styles.whitelistDesc}>{t('whitelistedTltip')}</div>
										</>
								}
							</div>
						}
						{
							(keywords && dropDown) &&
							<NotificationPopUp
								showToast={showToast}
								setShowToast={setShowToast}
								dropDown={dropDown}
							/>
						}
						{renderApiDetails()}
					</div >
			}
		</>
	);
};

export default ApiKeysEditForm;
